import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Mail, Send, Save, Eye, EyeOff, Lock, RefreshCw, CheckCircle2, XCircle,
  Users, List, ListPlus, Megaphone, Trash2, Search, LayoutTemplate,
} from "lucide-react";

const MODE_OPTIONS = [
  { value: "primary", label: "Primaire", desc: "SendGrid en premier, Resend en secours" },
  { value: "fallback", label: "Secours (recommandé)", desc: "Resend en premier, SendGrid si Resend échoue" },
  { value: "disabled", label: "Désactivé", desc: "N'utilise pas SendGrid" },
];

export default function AdminSendGrid() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    api_key: "", sender_email: "", sender_name: "", mode: "fallback",
    template_id_fr: "", template_id_en: "", template_id_de: "", template_id_sv: "",
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testSubject, setTestSubject] = useState("Test SendGrid depuis PAG");
  const [testTemplateId, setTestTemplateId] = useState("");
  const [logs, setLogs] = useState([]);

  // Marketing state
  const [lists, setLists] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState({ items: [], total: 0, hint: null });
  const [newContact, setNewContact] = useState({ email: "", first_name: "", last_name: "", list_ids: [] });
  const [addingContact, setAddingContact] = useState(false);
  const [sends, setSends] = useState([]);
  const [newSend, setNewSend] = useState({ name: "", subject: "", html_content: "", list_ids: [], send_at: "" });
  const [creatingSend, setCreatingSend] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/admin/sendgrid/settings"),
        api.get("/admin/sendgrid/logs?limit=25"),
      ]);
      setCfg(s);
      setForm({
        api_key: "",
        sender_email: s.sender_email || "",
        sender_name: s.sender_name || "",
        mode: s.mode || "fallback",
        template_id_fr: s.template_id_fr || "",
        template_id_en: s.template_id_en || "",
        template_id_de: s.template_id_de || "",
        template_id_sv: s.template_id_sv || "",
      });
      setLogs(l || []);
      // Charger Marketing seulement si SendGrid est activé
      if (s.enabled) {
        loadMarketing();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadMarketing = async () => {
    try {
      const [ll, ss] = await Promise.all([
        api.get("/admin/sendgrid/lists"),
        api.get("/admin/sendgrid/single-sends"),
      ]);
      setLists(ll.data.lists || []);
      setSends(ss.data.sends || []);
    } catch (e) { /* silent - user peut ne pas encore avoir SG configuré */ }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/sendgrid/settings", form);
      toast.success("Configuration SendGrid enregistrée (clé chiffrée Fernet)");
      setForm({ ...form, api_key: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Renseignez une adresse destinataire"); return; }
    setTesting(true);
    try {
      const body = { to: testTo, subject: testSubject };
      if (testTemplateId) { body.template_id = testTemplateId; body.template_data = { subject: testSubject, name: "Testeur PAG" }; }
      const { data } = await api.post("/admin/sendgrid/test-email", body);
      if (data.ok) toast.success(`✓ Email envoyé via SendGrid (${data.status_code})`);
      else toast.error(`✗ Échec : ${data.reason || data.error}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      const { data } = await api.post("/admin/sendgrid/lists", { name: newListName.trim() });
      if (data.ok) { toast.success(`List "${newListName}" créée`); setNewListName(""); await loadMarketing(); }
      else toast.error(`Échec : ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreatingList(false); }
  };

  const deleteList = async (list_id, name) => {
    if (!window.confirm(`Supprimer la list "${name}" ?`)) return;
    try {
      const { data } = await api.delete(`/admin/sendgrid/lists/${list_id}`);
      if (data.ok) { toast.success("List supprimée"); await loadMarketing(); }
      else toast.error(`Échec : ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const searchContacts = async () => {
    try {
      const { data } = await api.get(`/admin/sendgrid/contacts?query=${encodeURIComponent(contactSearch)}`);
      setContacts({ items: data.contacts || [], total: data.total || 0, hint: data.hint || null });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addContact = async () => {
    if (!newContact.email) { toast.error("Email requis"); return; }
    setAddingContact(true);
    try {
      const { data } = await api.post("/admin/sendgrid/contacts", newContact);
      if (data.ok) { toast.success(`Contact ${newContact.email} — upsert lancé (job ${data.job_id?.slice(0, 8)}…)`); setNewContact({ email: "", first_name: "", last_name: "", list_ids: [] }); }
      else toast.error(`Échec : ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingContact(false); }
  };

  const createSend = async () => {
    if (!newSend.name || !newSend.subject || !newSend.html_content || newSend.list_ids.length === 0) {
      toast.error("Renseignez nom, sujet, HTML et au moins une list"); return;
    }
    setCreatingSend(true);
    try {
      const payload = { ...newSend };
      if (!payload.send_at) delete payload.send_at;
      const { data } = await api.post("/admin/sendgrid/single-sends", payload);
      if (data.ok) {
        toast.success(`Campagne créée${data.scheduled ? " · programmée" : " · en brouillon"}`);
        setNewSend({ name: "", subject: "", html_content: "", list_ids: [], send_at: "" });
        await loadMarketing();
      } else toast.error(`Échec : ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreatingSend(false); }
  };

  const toggleListForContact = (id) => {
    setNewContact((c) => c.list_ids.includes(id)
      ? { ...c, list_ids: c.list_ids.filter(x => x !== id) }
      : { ...c, list_ids: [...c.list_ids, id] });
  };

  const toggleListForSend = (id) => {
    setNewSend((s) => s.list_ids.includes(id)
      ? { ...s, list_ids: s.list_ids.filter(x => x !== id) }
      : { ...s, list_ids: [...s.list_ids, id] });
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="admin-sendgrid">
      <PageHeader title="Configuration SendGrid" subtitle="Provider email alternatif (fallback Resend), Dynamic Templates multilingues et campagnes Marketing." />

      {/* Statut */}
      <div className={`p-4 border-2 flex items-center gap-3 ${cfg?.enabled ? "bg-emerald-50 border-emerald-500" : "bg-orange-50 border-orange-400"}`} data-testid="sg-status">
        {cfg?.enabled ? <CheckCircle2 className="h-6 w-6 text-[#059669]" /> : <XCircle className="h-6 w-6 text-[#F59E0B]" />}
        <div className="flex-1">
          <div className="font-display font-bold">{cfg?.enabled ? `SendGrid opérationnel (mode : ${cfg.mode})` : "SendGrid non configuré"}</div>
          <div className="text-xs text-[#495057]">
            {cfg?.enabled
              ? `Expéditeur : ${cfg.sender_name || ""} <${cfg.sender_email}> · Clé : ${cfg.api_key_masked}`
              : "Renseignez la clé API SendGrid (SG.xxx) + une adresse d'expéditeur vérifiée pour activer."}
          </div>
        </div>
      </div>

      {/* Formulaire config */}
      <div className="p-5 bg-white border border-[#DEE2E6]">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#0070BA]" />
          Identifiants SendGrid
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-[10px] uppercase tracking-wider">Clé API *</Label>
              <span data-testid="sg-key-encrypted" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#059669] text-white px-1.5 py-0.5">
                <Lock className="h-2.5 w-2.5" /> Chiffré Fernet
              </span>
            </div>
            <div className="text-[10px] text-[#868E96] mb-1">
              {cfg?.has_api_key ? <>Actuelle : <code className="text-[#0A0A0A]">{cfg.api_key_masked}</code>. Laissez vide pour conserver.</> : "Aucune clé — commence par SG.xxxxx (SendGrid Dashboard → Settings → API Keys)."}
            </div>
            <div className="relative">
              <Input type={showKey ? "text" : "password"} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="SG.xxxxxxxxxxxxxxxxxxxxxx" data-testid="sg-api-key" />
              <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#495057]">
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Adresse expéditeur * (vérifiée SG)</Label>
            <div className="text-[10px] text-[#868E96] mb-1">Doit être un Single Sender ou domaine authentifié côté SendGrid</div>
            <Input value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="noreply@parcavenue.eu" data-testid="sg-sender-email" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Nom d'expéditeur</Label>
            <div className="text-[10px] text-[#868E96] mb-1">Affiché comme "De : Nom" chez le destinataire</div>
            <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="PAG Business Manager" data-testid="sg-sender-name" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider">Mode d'utilisation</Label>
            <div className="grid md:grid-cols-3 gap-2 mt-1">
              {MODE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, mode: opt.value })}
                  data-testid={`sg-mode-${opt.value}`}
                  className={`text-left p-3 border-2 transition ${form.mode === opt.value ? "border-[#0070BA] bg-[#EFF6FF]" : "border-[#DEE2E6] bg-white hover:border-[#ADB5BD]"}`}
                >
                  <div className="font-bold text-sm">{opt.label}</div>
                  <div className="text-[10px] text-[#495057]">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dynamic Templates */}
        <div className="mt-6 border-t border-[#DEE2E6] pt-4">
          <div className="font-display font-bold flex items-center gap-2 mb-2">
            <LayoutTemplate className="h-4 w-4 text-[#7C3AED]" />
            Dynamic Templates par langue
          </div>
          <div className="text-[11px] text-[#495057] mb-3">
            Renseignez un Template ID SendGrid (<code>d-xxxxx</code>) par langue pour que les emails soient rendus depuis un template <b>natif SendGrid</b> multilingue.
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {["fr", "en", "de", "sv"].map(lang => (
              <div key={lang}>
                <Label className="text-[10px] uppercase tracking-wider">Template {lang.toUpperCase()}</Label>
                <Input
                  value={form[`template_id_${lang}`]}
                  onChange={(e) => setForm({ ...form, [`template_id_${lang}`]: e.target.value })}
                  placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  data-testid={`sg-template-${lang}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} data-testid="sg-save" className="bg-[#0070BA] text-white">
            <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
          <Button variant="outline" onClick={load} data-testid="sg-refresh"><RefreshCw className="h-4 w-4 mr-1" />Recharger</Button>
        </div>
      </div>

      {/* Test email */}
      <div className="p-5 bg-white border border-[#DEE2E6]">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Send className="h-5 w-5 text-[#059669]" />
          Envoyer un email de test
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Destinataire *</Label>
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="ex: test@parcavenue.eu" data-testid="sg-test-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Sujet</Label>
            <Input value={testSubject} onChange={(e) => setTestSubject(e.target.value)} data-testid="sg-test-subject" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider">Template ID (facultatif — si présent envoie via Dynamic Template)</Label>
            <Input value={testTemplateId} onChange={(e) => setTestTemplateId(e.target.value)} placeholder="d-xxx…" data-testid="sg-test-template" />
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={sendTest} disabled={testing || !cfg?.enabled} data-testid="sg-send-test" className="bg-[#059669] text-white">
            <Send className="h-4 w-4 mr-1" />{testing ? "Envoi…" : "Envoyer test"}
          </Button>
          {!cfg?.enabled && <span className="ml-2 text-xs text-[#F59E0B]">Configurer SendGrid d'abord</span>}
        </div>
      </div>

      {/* Marketing — Lists */}
      {cfg?.enabled && (
        <div className="p-5 bg-white border border-[#DEE2E6]">
          <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <List className="h-5 w-5 text-[#7C3AED]" />
            Lists Marketing ({lists.length})
          </div>
          <div className="flex gap-2 mb-3">
            <Input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="Nom de la nouvelle list" data-testid="sg-new-list-name" />
            <Button onClick={createList} disabled={creatingList || !newListName.trim()} data-testid="sg-create-list" className="bg-[#7C3AED] text-white">
              <ListPlus className="h-4 w-4 mr-1" />Créer
            </Button>
          </div>
          {lists.length === 0 ? (
            <div className="text-xs text-[#868E96]">Aucune list — créez-en une pour commencer.</div>
          ) : (
            <div className="divide-y divide-[#DEE2E6] max-h-64 overflow-y-auto border border-[#DEE2E6]">
              {lists.map(l => (
                <div key={l.id} className="p-2 flex items-center gap-2 text-sm hover:bg-[#F8F9FA]" data-testid={`sg-list-row-${l.id}`}>
                  <div className="flex-1"><b>{l.name}</b> <span className="text-[10px] text-[#868E96]">· {l.contact_count ?? 0} contacts</span></div>
                  <div className="text-[10px] font-mono text-[#868E96]">{l.id?.slice(0, 8)}…</div>
                  <Button size="sm" variant="outline" onClick={() => deleteList(l.id, l.name)} data-testid={`sg-delete-list-${l.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Marketing — Contacts */}
      {cfg?.enabled && (
        <div className="p-5 bg-white border border-[#DEE2E6]">
          <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-[#0070BA]" />
            Contacts
          </div>
          <div className="grid md:grid-cols-4 gap-2 mb-3">
            <Input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="email@example.com" data-testid="sg-new-contact-email" />
            <Input value={newContact.first_name} onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })} placeholder="Prénom" data-testid="sg-new-contact-first" />
            <Input value={newContact.last_name} onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })} placeholder="Nom" data-testid="sg-new-contact-last" />
            <Button onClick={addContact} disabled={addingContact} className="bg-[#0070BA] text-white" data-testid="sg-add-contact">
              {addingContact ? "…" : "Ajouter / Upsert"}
            </Button>
          </div>
          {lists.length > 0 && (
            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-wider mb-1 block">Assigner aux lists</Label>
              <div className="flex flex-wrap gap-1.5">
                {lists.map(l => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggleListForContact(l.id)}
                    data-testid={`sg-toggle-list-contact-${l.id}`}
                    className={`text-[11px] px-2 py-1 border ${newContact.list_ids.includes(l.id) ? "bg-[#0070BA] text-white border-[#0070BA]" : "bg-white border-[#DEE2E6]"}`}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 mb-3">
            <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Rechercher par email (partiel)" data-testid="sg-contact-search" />
            <Button onClick={searchContacts} variant="outline" data-testid="sg-contact-search-btn"><Search className="h-4 w-4 mr-1" />Rechercher</Button>
          </div>
          <div className="text-[11px] text-[#495057] mb-2">Total contacts SendGrid : <b>{contacts.total}</b>{contacts.hint && <> · {contacts.hint}</>}</div>
          {contacts.items.length > 0 && (
            <div className="divide-y divide-[#DEE2E6] max-h-64 overflow-y-auto border border-[#DEE2E6]">
              {contacts.items.map(c => (
                <div key={c.id} className="p-2 grid grid-cols-3 gap-2 text-sm hover:bg-[#F8F9FA]" data-testid={`sg-contact-row-${c.id}`}>
                  <div className="font-mono text-xs">{c.email}</div>
                  <div>{c.first_name} {c.last_name}</div>
                  <div className="text-[10px] text-[#868E96]">{(c.list_ids || []).length} list(s)</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Marketing — Campagnes (Single Sends) */}
      {cfg?.enabled && (
        <div className="p-5 bg-white border border-[#DEE2E6]">
          <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#F59E0B]" />
            Campagnes (Single Sends)
          </div>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Nom de la campagne *</Label>
              <Input value={newSend.name} onChange={(e) => setNewSend({ ...newSend, name: e.target.value })} placeholder="Newsletter janvier" data-testid="sg-send-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Sujet *</Label>
              <Input value={newSend.subject} onChange={(e) => setNewSend({ ...newSend, subject: e.target.value })} placeholder="Actualités PAG" data-testid="sg-send-subject" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider">HTML *</Label>
              <Textarea value={newSend.html_content} onChange={(e) => setNewSend({ ...newSend, html_content: e.target.value })} rows={5} placeholder="<h1>Bonjour…</h1>" data-testid="sg-send-html" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Programmer pour (facultatif · ISO)</Label>
              <Input value={newSend.send_at} onChange={(e) => setNewSend({ ...newSend, send_at: e.target.value })} placeholder="2026-12-31T09:00:00Z" data-testid="sg-send-at" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Lists cibles *</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {lists.length === 0 && <span className="text-[10px] text-[#868E96]">Créez une list d'abord.</span>}
                {lists.map(l => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggleListForSend(l.id)}
                    data-testid={`sg-toggle-list-send-${l.id}`}
                    className={`text-[11px] px-2 py-1 border ${newSend.list_ids.includes(l.id) ? "bg-[#F59E0B] text-white border-[#F59E0B]" : "bg-white border-[#DEE2E6]"}`}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Button onClick={createSend} disabled={creatingSend} className="bg-[#F59E0B] text-white" data-testid="sg-create-send">
            <Megaphone className="h-4 w-4 mr-1" />{creatingSend ? "Création…" : (newSend.send_at ? "Programmer" : "Sauver brouillon")}
          </Button>
          {sends.length > 0 && (
            <div className="mt-4 divide-y divide-[#DEE2E6] max-h-64 overflow-y-auto border border-[#DEE2E6]">
              {sends.map(s => (
                <div key={s.id} className="p-2 grid grid-cols-4 gap-2 text-xs hover:bg-[#F8F9FA]" data-testid={`sg-send-row-${s.id}`}>
                  <div className="font-bold col-span-2">{s.name}</div>
                  <div className="uppercase tracking-wider text-[10px] font-bold">{s.status}</div>
                  <div className="text-[10px] text-[#868E96] font-mono">{s.id?.slice(0, 8)}…</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Logs */}
      <div className="p-5 bg-white border border-[#DEE2E6]">
        <div className="font-display font-bold text-lg mb-3">Derniers emails SendGrid ({logs.length})</div>
        {logs.length === 0 ? (
          <div className="text-xs text-[#868E96]">Aucun email envoyé pour le moment.</div>
        ) : (
          <div className="divide-y divide-[#DEE2E6] max-h-72 overflow-y-auto border border-[#DEE2E6]">
            {logs.map((l) => (
              <div key={l.id} className="p-2 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs hover:bg-[#F8F9FA]" data-testid={`sg-log-${l.id}`}>
                <div className="text-[10px] text-[#868E96] font-mono">{l.created_at?.slice(0, 16).replace("T", " ")}</div>
                <div className="font-mono">{l.to}</div>
                <div className={`text-[10px] uppercase tracking-wider font-bold inline-block px-1.5 py-0.5 ${l.ok ? "bg-[#059669] text-white" : "bg-[#E60000] text-white"}`}>
                  {l.ok ? "OK" : "KO"} · {l.status_code || "-"}
                </div>
                <div className="truncate text-[#495057]">{l.subject}</div>
                <div className="truncate text-[10px] text-[#868E96]">{l.template_id ? `Tmpl ${l.template_id.slice(0, 12)}…` : "html"}{l.error ? ` · ${l.error}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
